import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    MenuItem,
    DialogContentText
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, SportsSoccer as SportsSoccerIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import PlayerCoachConfirmation from '../components/popups/PlayerCoachConfirmation';

const ChildForm = ({ open, onClose, onSubmit, loading }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        gender: ''
    });
    const [errors, setErrors] = useState({});

    const toTitleCase = (str) => {
        return str
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'firstName' || name === 'lastName' ? toTitleCase(value) : value
        }));
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};
        
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSubmit(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Child</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterLuxon}>
                                <DatePicker
                                    label="Date of Birth"
                                    value={formData.dateOfBirth}
                                    onChange={(newValue) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            dateOfBirth: newValue
                                        }));
                                        setErrors(prev => ({
                                            ...prev,
                                            dateOfBirth: ''
                                        }));
                                    }}
                                    maxDate={DateTime.now()}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                            error: !!errors.dateOfBirth,
                                            helperText: errors.dateOfBirth
                                        }
                                    }}
                                    disabled={loading}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                required
                                fullWidth
                                label="Gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                error={!!errors.gender}
                                helperText={errors.gender}
                                disabled={loading}
                            >
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : 'Add Child'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ConfirmClaimModal = ({ open, onClose, onConfirm, child, loading, isClaimed }) => {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Child Found</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    We found an existing child with these details:
                    <Box component="div" sx={{ mt: 2, mb: 2 }}>
                        <Typography><strong>Name:</strong> {child?.firstName} {child?.lastName}</Typography>
                        <Typography><strong>Date of Birth:</strong> {child ? DateTime.fromISO(child.dateOfBirth).toLocaleString(DateTime.DATE_SHORT) : ''}</Typography>
                        <Typography><strong>Gender:</strong> {child?.gender}</Typography>
                    </Box>
                    {isClaimed ? (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This child is already registered with another parent. If you believe this is an error, please contact us for assistance.
                        </Alert>
                    ) : (
                        'Is this your child?'
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {isClaimed ? (
                    <Button onClick={onClose}>Close</Button>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={loading}>No, Create New</Button>
                        <Button 
                            onClick={onConfirm} 
                            variant="contained" 
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Yes, This is My Child'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

const Children = () => {
    const { user, loading: authLoading } = useAuth();
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [coachingDialogOpen, setCoachingDialogOpen] = useState(false);
    const [coachingLoading, setCoachingLoading] = useState(false);
    const [claimModalOpen, setClaimModalOpen] = useState(false);
    const [childToClaim, setChildToClaim] = useState(null);
    const [claimData, setClaimData] = useState(null);
    const [settings, setSettings] = useState({});

    const isCoachingOrPlaying = useMemo(() => {
        return user?.isCoachingOrPlaying || false;
    }, [user?.isCoachingOrPlaying]);

    const showCoachingButton = useMemo(() => {
        return !isCoachingOrPlaying && children.length > 0;
    }, [isCoachingOrPlaying, children]);

    const handleSetCoachingPlaying = async (data) => {
        try {
            setCoachingLoading(true);
            setError(null);
            setSuccess(null);

            // Refresh dashboard data
            const response = await api.get('/users/dashboard');
            const { client, children: childrenData } = response.data.data;
            setChildren(childrenData || []);
            
            setSuccess(data.message);
            setCoachingDialogOpen(false);
        } catch (error) {
            setError(error.response?.data?.message || 'An error occurred');
        } finally {
            setCoachingLoading(false);
        }
    };

    const fetchChildren = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.getChildren();
            setChildren(response.data.children || []);
        } catch (error) {
            setError('Failed to fetch children');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            if (user?.id) {
                fetchChildren();
            } else {
                setLoading(false);
            }
        }
    }, [user?.id, authLoading]);

    const handleAddChild = async (childData) => {
        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);

            // First check if child exists
            try {
                const checkResponse = await api.post('/users/children/check', {
                    firstName: childData.firstName,
                    lastName: childData.lastName,
                    dateOfBirth: childData.dateOfBirth.toISODate(),
                    gender: childData.gender
                });

                // Show modal for both claimed and unclaimed children
                setChildToClaim(checkResponse.data.child);
                setClaimData(childData);
                setClaimModalOpen(true);
                return;

            } catch (error) {
                // If child doesn't exist (404), create new one
                if (error.response?.status === 404) {
                    await createNewChild(childData);
                    return;
                }

                // Handle other errors
                setError(error.response?.data?.message || 'An unexpected error occurred');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const createNewChild = async (childData) => {
        try {
            setSubmitting(true);
            const createResponse = await api.post('/users/children', {
                firstName: childData.firstName,
                lastName: childData.lastName,
                dateOfBirth: childData.dateOfBirth.toISODate(),
                gender: childData.gender
            });

            await fetchChildren();
            setFormOpen(false);
            setSuccess('Child added successfully');
        } catch (createError) {
            setError(createError.response?.data?.message || 'Failed to create child');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClaimConfirm = async () => {
        try {
            setSubmitting(true);
            const claimResponse = await api.post('/users/children/claim', {
                firstName: claimData.firstName,
                lastName: claimData.lastName,
                dateOfBirth: claimData.dateOfBirth.toISODate(),
                gender: claimData.gender
            });
            
            await fetchChildren();
            setClaimModalOpen(false);
            setFormOpen(false);
            setSuccess('Child claimed successfully');
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to claim child');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClaimCancel = async () => {
        // Only create a new child if the existing child was not claimed
        if (claimData && !childToClaim?.parent) {
            await createNewChild(claimData);
        } else {
            // If child is claimed, close both modals
            setFormOpen(false);
        }
        setClaimModalOpen(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_SHORT);
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data.data);
            } catch (error) {
                console.error('Failed to fetch settings:', error);
            }
        };
        fetchSettings();
    }, []);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Children
            </Typography>

            {settings.motd_children && (
                <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                >
                    <div dangerouslySetInnerHTML={{ __html: settings.motd_children }} />
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setFormOpen(true)}
                        >
                            Add Child
                        </Button>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Refresh list">
                            <IconButton onClick={() => fetchChildren()} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    {showCoachingButton && (
                        <Grid item>
                            <Button
                                variant="outlined"
                                startIcon={<SportsSoccerIcon />}
                                onClick={() => setCoachingDialogOpen(true)}
                            >
                                I'm a Coach/Player
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Date of Birth</TableCell>
                            <TableCell>Gender</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : children.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    No children found
                                </TableCell>
                            </TableRow>
                        ) : (
                            children.map((child) => (
                                <TableRow key={child._id}>
                                    <TableCell>
                                        {child.firstName} {child.lastName}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(child.dateOfBirth)}
                                    </TableCell>
                                    <TableCell>
                                        {child.gender}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ChildForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleAddChild}
                loading={submitting}
            />

            <ConfirmClaimModal
                open={claimModalOpen}
                onClose={handleClaimCancel}
                onConfirm={handleClaimConfirm}
                child={childToClaim}
                loading={submitting}
                isClaimed={childToClaim?.parent}
            />

            <PlayerCoachConfirmation
                open={coachingDialogOpen}
                onClose={() => setCoachingDialogOpen(false)}
                onSuccess={handleSetCoachingPlaying}
                loading={coachingLoading}
            />
        </Box>
    );
};

export default Children; 